import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from './config';
import { dogService } from './dogService';
import {
  VaccinationRecord,
  VaccinationFormData,
  RABIES_VALID_DAYS,
} from '@/types/vaccination';

/**
 * ワクチン接種記録管理サービスクラス
 *
 * `dogs/{dogId}/vaccinations/{vaccinationId}` サブコレクションを操作する。
 */
export class VaccinationService {
  private readonly DOGS_COLLECTION = 'dogs';
  private readonly SUBCOLLECTION = 'vaccinations';

  /**
   * サブコレクション参照を取得
   */
  private vaccinationsRef(dogId: string) {
    return collection(db, this.DOGS_COLLECTION, dogId, this.SUBCOLLECTION);
  }

  /**
   * 次回期限を計算する。
   * - 明示指定があればそれを優先
   * - rabies は接種日 + 365 日で自動計算
   * - それ以外は未設定（undefined）
   */
  private resolveNextDueDate(data: VaccinationFormData): Date | undefined {
    if (data.nextDueDate) {
      return data.nextDueDate;
    }
    if (data.type === 'rabies') {
      const due = new Date(data.vaccinatedAt);
      due.setDate(due.getDate() + RABIES_VALID_DAYS);
      return due;
    }
    return undefined;
  }

  /**
   * 接種記録の作成
   */
  async create(
    dogId: string,
    data: VaccinationFormData,
    userId: string
  ): Promise<VaccinationRecord> {
    try {
      // 親 Dog の所有確認
      const dog = await dogService.getDog(dogId, userId);
      if (!dog) {
        throw new Error('個体が見つかりません');
      }

      const recordRef = doc(this.vaccinationsRef(dogId));
      const now = Timestamp.now();
      const nextDue = this.resolveNextDueDate(data);

      const record: VaccinationRecord = {
        id: recordRef.id,
        dogId,
        ownerId: userId,
        type: data.type,
        vaccinatedAt: Timestamp.fromDate(data.vaccinatedAt),
        nextDueDate: nextDue ? Timestamp.fromDate(nextDue) : undefined,
        clinicName: data.clinicName,
        notes: data.notes,
        createdAt: now,
        updatedAt: now,
        createdBy: userId,
      };

      // undefined を除去（Firestore は undefined を許可しない）
      const cleaned = Object.fromEntries(
        Object.entries(record).filter(([, value]) => value !== undefined)
      ) as VaccinationRecord;

      await setDoc(recordRef, cleaned);
      return cleaned;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`ワクチン接種記録の作成に失敗しました: ${error.message}`);
      }
      throw new Error('ワクチン接種記録の作成に失敗しました');
    }
  }

  /**
   * 指定個体の接種記録一覧取得（接種日の降順）
   */
  async list(dogId: string, userId: string): Promise<VaccinationRecord[]> {
    try {
      // 親 Dog の所有確認
      const dog = await dogService.getDog(dogId, userId);
      if (!dog) {
        throw new Error('個体が見つかりません');
      }

      const q = query(this.vaccinationsRef(dogId), orderBy('vaccinatedAt', 'desc'));
      const snapshot = await getDocs(q);

      const records: VaccinationRecord[] = [];
      snapshot.forEach((d) => records.push(d.data() as VaccinationRecord));
      return records;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`ワクチン接種記録の取得に失敗しました: ${error.message}`);
      }
      throw new Error('ワクチン接種記録の取得に失敗しました');
    }
  }

  /**
   * 接種記録の更新
   */
  async update(
    dogId: string,
    vaccinationId: string,
    data: VaccinationFormData,
    userId: string
  ): Promise<void> {
    try {
      const dog = await dogService.getDog(dogId, userId);
      if (!dog) {
        throw new Error('個体が見つかりません');
      }

      const recordRef = doc(this.vaccinationsRef(dogId), vaccinationId);
      const snap = await getDoc(recordRef);
      if (!snap.exists()) {
        throw new Error('ワクチン接種記録が見つかりません');
      }

      const nextDue = this.resolveNextDueDate(data);

      const updateData: Record<string, unknown> = {
        type: data.type,
        vaccinatedAt: Timestamp.fromDate(data.vaccinatedAt),
        nextDueDate: nextDue ? Timestamp.fromDate(nextDue) : null,
        clinicName: data.clinicName ?? null,
        notes: data.notes ?? null,
        updatedAt: Timestamp.now(),
      };

      await updateDoc(recordRef, updateData);
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`ワクチン接種記録の更新に失敗しました: ${error.message}`);
      }
      throw new Error('ワクチン接種記録の更新に失敗しました');
    }
  }

  /**
   * 接種記録の削除
   */
  async delete(dogId: string, vaccinationId: string, userId: string): Promise<void> {
    try {
      const dog = await dogService.getDog(dogId, userId);
      if (!dog) {
        throw new Error('個体が見つかりません');
      }

      const recordRef = doc(this.vaccinationsRef(dogId), vaccinationId);
      await deleteDoc(recordRef);
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`ワクチン接種記録の削除に失敗しました: ${error.message}`);
      }
      throw new Error('ワクチン接種記録の削除に失敗しました');
    }
  }

  /**
   * 期限切れ・期限間近の接種記録を横断取得する。
   *
   * コレクショングループ検索で owner の全ワクチン記録を取得し、
   * `nextDueDate` が「期限超過」または「withinDays 日以内」のものを返す。
   */
  async listOverdueOrUpcoming(
    userId: string,
    withinDays: number
  ): Promise<VaccinationRecord[]> {
    try {
      const today = new Date();
      // 上限 = 今日 + withinDays 日の終端。日単位の最終判定はクライアント側で行う
      const dueLimit = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() + withinDays,
        23,
        59,
        59,
        999
      );

      // orderBy がないと ownerId 単一フィールドの COLLECTION_GROUP インデックス
      // （exemption）が必要になるため、複合インデックス (ownerId, nextDueDate) に
      // 合致するよう orderBy を必ず含める
      const q = query(
        collectionGroup(db, this.SUBCOLLECTION),
        where('ownerId', '==', userId),
        where('nextDueDate', '<=', Timestamp.fromDate(dueLimit)),
        orderBy('nextDueDate', 'asc')
      );
      const snapshot = await getDocs(q);

      const upcoming: VaccinationRecord[] = [];

      snapshot.forEach((d) => {
        const record = d.data() as VaccinationRecord;
        if (!record.nextDueDate) return;

        const due = record.nextDueDate.toDate();
        const msPerDay = 1000 * 60 * 60 * 24;
        const daysUntilDue = Math.round(
          (new Date(due.getFullYear(), due.getMonth(), due.getDate()).getTime() -
            new Date(
              today.getFullYear(),
              today.getMonth(),
              today.getDate()
            ).getTime()) /
            msPerDay
        );

        if (daysUntilDue <= withinDays) {
          upcoming.push(record);
        }
      });

      // クエリの orderBy('nextDueDate', 'asc') により期限昇順で返る
      return upcoming;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(
          `期限間近のワクチン記録の取得に失敗しました: ${error.message}`
        );
      }
      throw new Error('期限間近のワクチン記録の取得に失敗しました');
    }
  }
}

// シングルトンインスタンスをエクスポート
export const vaccinationService = new VaccinationService();
